// Step 4 workspace: create dialog links and manage respondent invites. Was the
// CreateDialogDialog modal; promoted to a step view so the journey rail can
// route here like every other step.

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import { cn, formatDate } from '@/lib/utils';
import { useDialogs, useCreateDialog, useDeleteDialog, useRespondents, useAddRespondents, useResendInvite, useRevokeRespondent, useUpdateRespondentContext } from '@/hooks/use-dialog';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { toast } from '@/hooks/use-toast';
import { Copy, Check, Link, Loader2, Trash2, ChevronDown, ChevronRight, Send, UserX, RefreshCw, Mail, Plus } from 'lucide-react';
import { WORKSPACE_WIDTH } from '@/lib/workspace-width';
import { StepHeader } from '@/features/project/StepHeader';
import { useUIStore } from '@/stores';
import type { DialogSession, DialogRespondent, RespondentContext } from '@/types/dialog';
import { OpenerPreview } from './OpenerPreview';
import { FramingCard } from '@/features/idea/FramingCard';
import { SessionsSection } from './SessionsSection';
import { PlanLimitError, planLimitMessage, allowanceLabel, CONTACT_PATH } from '@/lib/plan-limit';
import { countFreshInvites, parseInviteEmails } from '@/lib/invite-allowance';

interface DialogViewProps {
  projectId: string;
}

const EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const STATUS_STYLES: Record<string, string> = {
  invited: 'bg-gray-100 text-gray-700',
  started: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  revoked: 'bg-red-100 text-red-700 line-through',
};

const EMPTY_CONTEXT: RespondentContext = {};

// `now` is read fresh on every render on purpose: a link stored 'active' can
// already be past its expiresAt, because expiry here is lazy (see
// lambda/shared/dialog-links.ts), so this derivation needs real wall-clock
// time rather than a memoised one. The call is deliberately non-deterministic
// across renders. This indirection satisfies react-hooks/purity's call-site
// check but not its intent, which is accepted here because no React Compiler
// is wired into this build, so nothing can silently cache a stale value.
const readNow = () => Date.now();

function ContextFields({
  value,
  onChange,
  size = 'sm',
}: {
  value: RespondentContext;
  onChange: (next: RespondentContext) => void;
  size?: 'sm' | 'xs';
}) {
  const cls = size === 'xs'
    ? 'w-full px-2 py-1 border rounded-md bg-background text-xs focus:outline-hidden focus:ring-2 focus:ring-primary/50'
    : 'w-full px-2.5 py-1.5 border rounded-md bg-background text-xs focus:outline-hidden focus:ring-2 focus:ring-primary/50';
  const labelCls = 'text-xs uppercase tracking-wide text-muted-foreground';
  const set = (key: keyof RespondentContext) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, [key]: e.target.value });
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="space-y-0.5">
        <span className={labelCls}>Role</span>
        <input className={cls} value={value.role ?? ''} onChange={set('role')} placeholder="e.g. Senior PM" maxLength={200} />
      </label>
      <label className="space-y-0.5">
        <span className={labelCls}>Team</span>
        <input className={cls} value={value.team ?? ''} onChange={set('team')} placeholder="e.g. Growth" maxLength={200} />
      </label>
      <label className="space-y-0.5">
        <span className={labelCls}>Company</span>
        <input className={cls} value={value.company ?? ''} onChange={set('company')} placeholder="e.g. Acme" maxLength={200} />
      </label>
    </div>
  );
}

function hasAnyContext(c: RespondentContext | undefined): boolean {
  if (!c) return false;
  return Boolean((c.role ?? '').trim() || (c.team ?? '').trim() || (c.company ?? '').trim());
}

function RespondentRow({
  respondent,
  dialogId,
  projectId,
  expanded,
  onToggle,
}: {
  respondent: DialogRespondent;
  dialogId: string;
  projectId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const resendInvite = useResendInvite();
  const revokeRespondent = useRevokeRespondent();
  const updateContext = useUpdateRespondentContext();

  const [draft, setDraft] = useState<RespondentContext>(respondent.context ?? EMPTY_CONTEXT);
  // Keep the local draft in sync if the server-side context changes (e.g.
  // another tab edits it) while the row is collapsed.
  useEffect(() => {
    if (!expanded) setDraft(respondent.context ?? EMPTY_CONTEXT);
  }, [respondent.context, expanded]);

  const handleSave = async () => {
    try {
      await updateContext.mutateAsync({ dialogId, respondentId: respondent.id, projectId, context: draft });
    } catch {
      // Error surfaced via mutation state below
    }
  };

  return (
    <div className="rounded-md border border-transparent hover:border-muted">
      <div className="flex items-center gap-2 text-sm py-1 px-1">
        <button
          className="shrink-0 p-0.5 hover:bg-muted rounded"
          onClick={onToggle}
          title={expanded ? 'Hide context' : 'Edit context'}
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="truncate flex-1">{respondent.email}</span>
        {respondent.context?.role && (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 truncate max-w-32" title={respondent.context.role}>
            {respondent.context.role}
          </span>
        )}
        {respondent.context?.team && (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 truncate max-w-32" title={respondent.context.team}>
            {respondent.context.team}
          </span>
        )}
        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', STATUS_STYLES[respondent.status])}>
          {respondent.status}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          onClick={() => resendInvite.mutate({ dialogId, respondentId: respondent.id, projectId })}
          disabled={resendInvite.isPending}
          title="Resend invite"
        >
          <RefreshCw className="h-2.5 w-2.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={() => revokeRespondent.mutate({ dialogId, respondentId: respondent.id, projectId })}
          disabled={revokeRespondent.isPending}
          title="Revoke access"
        >
          <UserX className="h-2.5 w-2.5" />
        </Button>
      </div>
      {expanded && (
        <div className="px-2 pb-2 pt-1 space-y-2 bg-muted/30 rounded-md">
          <ContextFields value={draft} onChange={setDraft} size="xs" />
          <div className="flex items-center justify-end gap-2">
            {updateContext.isError && (
              <p className="text-[11px] text-destructive flex-1">Save failed — try again.</p>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[11px]"
              onClick={() => setDraft(respondent.context ?? EMPTY_CONTEXT)}
              disabled={updateContext.isPending}
            >
              Reset
            </Button>
            <Button
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={handleSave}
              disabled={updateContext.isPending}
            >
              {updateContext.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save context'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RespondentSection({
  dialog,
  projectId,
  remaining,
}: {
  dialog: DialogSession;
  projectId: string;
  remaining: number | null; // null = unlimited / unknown
}) {
  const [emailInput, setEmailInput] = useState('');
  const [contextOpen, setContextOpen] = useState(false);
  const [inviteContext, setInviteContext] = useState<RespondentContext>(EMPTY_CONTEXT);
  const [expandedRespondentId, setExpandedRespondentId] = useState<string | null>(null);
  const [capMessage, setCapMessage] = useState<string | null>(null);
  const { data: respondents = [], isLoading } = useRespondents(dialog.id, projectId);
  const addRespondents = useAddRespondents();
  const resendInvite = useResendInvite();
  const revokeRespondent = useRevokeRespondent();

  const handleInvite = async () => {
    const emails = parseInviteEmails(emailInput);
    if (!emails.length) return;
    // Client-side cap so a batch that can't fit never reaches the server. Only
    // addresses without an existing respondent row reserve anything, so gate on
    // that count rather than on how many were typed — otherwise refreshing an
    // existing respondent's context, or reactivating a revoked one, is blocked
    // at 0 remaining even though the server allows both for free. The server
    // enforces the real limit transactionally; this is the friendly copy.
    const freshCount = countFreshInvites({ emails, respondents });
    if (remaining !== null && freshCount > remaining) {
      setCapMessage(
        remaining === 0
          ? "This project has used all its conversations, so there's no room for a new invite. You can still update or re-invite someone already on the list."
          : `You can invite ${remaining} more ${remaining === 1 ? 'person' : 'people'} on this project. Trim the list or talk to us.`,
      );
      return;
    }
    setCapMessage(null);
    const sharedContext = hasAnyContext(inviteContext) ? inviteContext : undefined;
    try {
      await addRespondents.mutateAsync({
        dialogId: dialog.id,
        respondents: emails.map((email) => ({ email, context: sharedContext })),
        projectId,
      });
      setEmailInput('');
      setInviteContext(EMPTY_CONTEXT);
      setContextOpen(false);
    } catch {
      // Error surfaced below from addRespondents.error
    }
  };

  const activeRespondents = respondents.filter((r) => r.status !== 'revoked');
  const revokedRespondents = respondents.filter((r) => r.status === 'revoked');

  // What the addresses currently typed would actually reserve. Zero means the
  // batch only touches people already on the list, which stays allowed at the
  // cap, so the button must not be disabled on `remaining` alone.
  const freshInBox = countFreshInvites({ emails: parseInviteEmails(emailInput), respondents });
  const overAllowance = remaining !== null && freshInBox > remaining;

  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={emailInput}
          onChange={(e) => {
            setEmailInput(e.target.value);
            // The cap message describes the list as it was when submitted, so
            // editing the list makes it stale — trimming an address would
            // otherwise re-enable the button while the red text still said the
            // batch was too big.
            setCapMessage(null);
          }}
          placeholder="Enter emails (comma-separated)"
          className="flex-1 px-3 py-1.5 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/50"
          onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleInvite}
          disabled={!emailInput.trim() || addRespondents.isPending || overAllowance}
          className="shrink-0"
        >
          {addRespondents.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          Invite
        </Button>
      </div>
      <button
        type="button"
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setContextOpen((v) => !v)}
      >
        {contextOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Add context (optional)
        {!contextOpen && hasAnyContext(inviteContext) && (
          <span className="ml-1 px-1 py-0 rounded bg-primary/10 text-primary text-[10px]">staged</span>
        )}
      </button>
      {contextOpen && (
        <div className="space-y-2 p-2 rounded-md bg-muted/30">
          <p className="text-xs text-muted-foreground">
            Applied to every email in this batch. Helps the AI ask role-relevant follow-ups.
          </p>
          <ContextFields value={inviteContext} onChange={setInviteContext} size="xs" />
        </div>
      )}
      {capMessage && <p className="text-xs text-destructive">{capMessage}</p>}
      {addRespondents.isError && (
        <p className="text-xs text-destructive">
          {addRespondents.error instanceof PlanLimitError
            ? planLimitMessage(addRespondents.error.payload).description
            : 'Failed to send invites. Please try again.'}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : activeRespondents.length > 0 ? (
        <div className="space-y-0.5">
          {activeRespondents.map((r) => (
            <RespondentRow
              key={r.id}
              respondent={r}
              dialogId={dialog.id}
              projectId={projectId}
              expanded={expandedRespondentId === r.id}
              onToggle={() => setExpandedRespondentId(expandedRespondentId === r.id ? null : r.id)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-1">
          No respondents invited yet.
        </p>
      )}

      {resendInvite.isError && (
        <p className="text-xs text-destructive">Failed to resend invite. Please try again.</p>
      )}
      {revokeRespondent.isError && (
        <p className="text-xs text-destructive">Failed to revoke respondent. Please try again.</p>
      )}

      {revokedRespondents.length > 0 && (
        <details className="text-xs">
          <summary className="text-muted-foreground cursor-pointer">
            {revokedRespondents.length} revoked
          </summary>
          <div className="mt-1 space-y-1">
            {revokedRespondents.map((r) => (
              <div key={r.id} className="flex items-center gap-2 py-1 opacity-50">
                <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 line-through">{r.email}</span>
                <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', STATUS_STYLES[r.status])}>
                  revoked
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export function DialogView({ projectId }: DialogViewProps) {
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [expandedDialogId, setExpandedDialogId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: dialogs = [], isLoading } = useDialogs(projectId);
  const { data: project } = useProject(projectId);
  const createDialog = useCreateDialog();
  const deleteDialog = useDeleteDialog();
  const updateProject = useUpdateProject();
  const setViewMode = useUIStore((s) => s.setViewMode);

  // "Complete" = the PM judges they have enough dialogs for this iteration —
  // not a respondent-count inference. Re-open covers the next round.
  const reviewed = !!project?.dialogsReviewed;
  const setReviewed = async (value: boolean) => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { dialogsReviewed: value } });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  };

  const handleCreate = async () => {
    if (!label.trim()) return;
    try {
      await createDialog.mutateAsync({ projectId, label: label.trim(), expiresInDays });
      setLabel('');
      setCreating(false);
    } catch {
      // Error surfaced below from createDialog.error
    }
  };

  const handleCopy = async (token: string) => {
    const url = `${window.location.origin}${import.meta.env.BASE_URL}dialog/${token}`;
    await navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleClose = async (dialogId: string) => {
    try {
      await deleteDialog.mutateAsync({ id: dialogId, projectId });
    } catch {
      // Error handled by React Query
    }
  };

  const activeDialogs = dialogs.filter((d) => d.status === 'active');
  const closedDialogs = dialogs.filter((d) => d.status !== 'active');

  // Conversations allowance (owner's plan). Invited people reserve one each;
  // Shared-link visitors are deliberately NOT counted: the identity on that
  // public path is browser-minted and forgeable, so metering it would measure
  // something the server cannot enforce.
  const convUsed = project?.usage?.conversations ?? 0;
  const convLimit = project?.limits?.conversationsPerProject;
  const convLabel = allowanceLabel(convUsed, convLimit, 'conversation', 'conversations');
  const convRemaining = convLimit === null || convLimit === undefined ? null : Math.max(0, convLimit - convUsed);

  // Open links are derived, not counted: expiry is lazy, so a dialog stored as
  // active can already be past its expiry. Mirror the backend rule in
  // lambda/shared/dialog-links.ts, and keep the two in lockstep — the same way
  // the schema max-lengths in src/types/project.ts mirror the backend's.
  // The idea coach is a PM tool, not a respondent link, so it never counts.
  const now = readNow();
  const openLinks = dialogs.filter(
    (d) =>
      d.mode !== 'idea-coach' &&
      d.status !== 'closed' &&
      d.status !== 'expired' &&
      new Date(d.expiresAt).getTime() >= now,
  ).length;
  const linksLimit = project?.limits?.dialogsPerProject;
  const linksLabel = allowanceLabel(openLinks, linksLimit, 'open dialog', 'open dialogs');
  const atLinkLimit = linksLimit !== null && linksLimit !== undefined && openLinks >= linksLimit;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className={`${WORKSPACE_WIDTH} mx-auto space-y-6`}>

        <StepHeader
          projectId={projectId}
          stepId="dialog"
          actions={
            <div className="flex items-center gap-2 shrink-0">
              {!creating && (
                <Button
                  variant={reviewed ? 'outline' : 'brand'}
                  onClick={() => {
                    createDialog.reset();
                    setCreating(true);
                  }}
                  disabled={atLinkLimit}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New dialog
                </Button>
              )}
              {reviewed ? (
                <Button variant="outline" onClick={() => setReviewed(false)} disabled={updateProject.isPending}>
                  Re-open step
                </Button>
              ) : dialogs.length > 0 ? (
                <Button variant="outline" onClick={() => setReviewed(true)} disabled={updateProject.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark step complete
                </Button>
              ) : null}
            </div>
          }
        />

        {convLabel && (
          <p className="text-xs text-muted-foreground -mt-2">
            {convLabel}. Only people you invite by email count towards this.
            {convRemaining === 0 && (
              <>
                {' '}
                <a href={CONTACT_PATH} className="underline underline-offset-2">
                  Talk to us
                </a>{' '}
                if you need more.
              </>
            )}
          </p>
        )}

        {linksLabel && (
          <p className="text-xs text-muted-foreground -mt-2">
            {linksLabel}.
            {atLinkLimit && (
              <>
                {' '}Closing one frees a slot, or{' '}
                <a href={CONTACT_PATH} className="underline underline-offset-2">
                  talk to us
                </a>{' '}
                if you need more room.
              </>
            )}
          </p>
        )}

        {project && <FramingCard project={project} onOpenCoach={() => setViewMode('idea')} />}

        <div className="space-y-4">
          {/* Create form */}
          {creating && (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <div className="space-y-1">
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Design-partner PMs — July"
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/50"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                />
                <p className="text-xs text-muted-foreground">Internal label. Respondents never see it.</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                  className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/50"
                >
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>Expires in {opt.label}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!label.trim() || createDialog.isPending}
                >
                  {createDialog.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Link className="h-3 w-3" />
                  )}
                  Create
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCreating(false)} disabled={createDialog.isPending}>
                  Cancel
                </Button>
              </div>
              {createDialog.isError && (
                <p className="text-xs text-destructive">
                  {createDialog.error instanceof PlanLimitError
                    ? planLimitMessage(createDialog.error.payload).description
                    : 'Failed to create dialog. Please try again.'}
                </p>
              )}
            </div>
          )}

          {/* Active dialogs */}
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : activeDialogs.length > 0 ? (
            <div className="space-y-2">
              <SectionLabel>Active</SectionLabel>
              {activeDialogs.map((dialog) => {
                const isExpanded = expandedDialogId === dialog.id;
                return (
                  <div key={dialog.id} className="border rounded-lg bg-card shadow-xs">
                    <div className="flex items-center gap-2 p-3 text-sm">
                      <button
                        className="shrink-0 p-0.5 hover:bg-muted rounded"
                        onClick={() => setExpandedDialogId(isExpanded ? null : dialog.id)}
                      >
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{dialog.label}</p>
                        <p className="text-xs text-muted-foreground">
                          Expires {formatDate(dialog.expiresAt)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => handleCopy(dialog.token)}
                        title="Copy shared link"
                      >
                        {copiedToken === dialog.token ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleClose(dialog.id)}
                        disabled={deleteDialog.isPending}
                        title="Close dialog"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-3">
                        <OpenerPreview
                          dialog={dialog}
                          snapshotStale={
                            !!project &&
                            ((dialog.openerBody ?? '').trim() !== (project.openerBody ?? '').trim() ||
                              (dialog.respondentFraming ?? '').trim() !==
                                (project.respondentFraming ?? '').trim())
                          }
                        />
                        <RespondentSection dialog={dialog} projectId={projectId} remaining={convRemaining} />
                        <SessionsSection dialogId={dialog.id} projectId={projectId} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No active dialogs yet. Create one above.
            </p>
          )}
          {deleteDialog.isError && (
            <p className="text-xs text-destructive">Failed to close dialog. Please try again.</p>
          )}

          {/* Closed/expired dialogs */}
          {closedDialogs.length > 0 && (
            <details className="pt-1">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {closedDialogs.length} closed dialog{closedDialogs.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-2 space-y-2">
              {closedDialogs.map((dialog) => (
                <div key={dialog.id} className="border rounded-lg bg-card shadow-xs">
                  <div className="flex items-center gap-2 p-3 text-sm opacity-60">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{dialog.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {dialog.status === 'expired' ? 'Expired' : 'Closed'} {formatDate(dialog.expiresAt)}
                      </p>
                    </div>
                  </div>
                  {/* Not dimmed with the header above — closed links are the
                      main straggler case while drafts are still recoverable. */}
                  <SessionsSection dialogId={dialog.id} projectId={projectId} className="px-3 pb-3" />
                </div>
              ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
